<template>
  <!-- A v-on there will break the emitted events -->
  <label
    class="darwin-toggle"
    v-bind="$attrs"
    :for="id"
    :class="{
      'darwin-toggle--truthy': modelValue,
      'darwin-toggle--disabled': disabled,
      [`darwin-toggle--${kind}`]: true,
      [`darwin-toggle--${size}`]: true,
    }"
  >
    <input
      :id="id"
      :checked="modelValue"
      :disabled="disabled"
      :aria-checked="`${modelValue}`"
      class="darwin-toggle__native"
      type="checkbox"
      role="switch"
      @change="onChange"
      @keydown.space.prevent="onChange"
    />
    <div
      class="darwin-toggle__track"
      aria-hidden="true"
    >
      <div class="darwin-toggle__thumb"></div>
    </div>
    <span
      v-if="$slots.default"
      class="darwin-toggle__label"
    >
      <slot />
    </span>
  </label>
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    modelValue?: boolean
    /**
     * Optional id attribute which is bound to the input element
     */
    id?: string
    /**
     * Binds to the 'disabled' html attribute of the input element
     */
    disabled?: boolean
    kind: 'primary' | 'secondary' | 'negative'
    size: 'small' | 'large'
  }>(),
  { id: undefined, modelValue: false, disabled: false },
)

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void
}>()

const onChange = (): void => {
  if (!props.disabled) {
    emit('update:modelValue', !props.modelValue)
  }
}
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/index.scss';
@import '@/uiKit/assets/buttons.scss';

.darwin-toggle {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  color: var(--color-text-default);
  cursor: pointer;

  &--disabled {
    cursor: not-allowed;
  }

  &--truthy {
    .darwin-toggle__thumb {
      left: 100%;
      transform: translateX(-100%);
    }
  }

  &__native {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;

    &:focus-visible + .darwin-toggle__track {
      @include focusOutline;
    }
  }

  &__track {
    border-radius: 32px;
    position: relative;
    @include row;
    align-items: center;
    background: var(--color-background-gray-subtle);
  }
  &__thumb {
    transform: translateX(0);
    left: 0;
    position: absolute;
    transition:
      left 0.22s cubic-bezier(0.4, 0, 0.2, 1),
      transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
    &:before {
      content: '';
      display: block;
      background: #fff;
      border-radius: 100%;
      margin: 2px;
    }
    &:after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border-radius: 50%;
      background: currentColor;
      opacity: 0.12;
      transform: scale3d(0, 0, 1);
      transition: transform 0.22s cubic-bezier(0, 0, 0.2, 1);
      z-index: -1;
    }
  }

  &__label {
    user-select: none;
  }

  &:not(.darwin-toggle--disabled) {
    &:hover {
      .darwin-toggle__thumb {
        &:after {
          transform: scale3d(1.5, 1.5, 1);
        }
      }
    }
  }

  &--small {
    font-size: 13px;
    .darwin-toggle__track {
      height: 16px;
      width: 28px;
    }
    .darwin-toggle__thumb:before {
      width: 12px;
      height: 12px;
    }
  }

  &--large {
    .darwin-toggle__track {
      height: 20px;
      width: 36px;
    }
    .darwin-toggle__thumb:before {
      width: 14px;
      height: 14px;
    }
  }

  &--primary {
    &.darwin-toggle--truthy {
      .darwin-toggle__track {
        background: var(--color-background-primary);
      }
    }
  }

  &--secondary {
    &.darwin-toggle--truthy {
      .darwin-toggle__track {
        background: var(--color-background-gray-subtlest);
      }
    }
  }

  &--negative {
    &.darwin-toggle--truthy {
      .darwin-toggle__track {
        background: $colorInteractiveNegativeDefault;
      }
    }
  }

  &--disabled {
    pointer-events: none;
    &.darwin-toggle--truthy {
      .darwin-toggle__track {
        background: var(--color-background-gray-subtle);
      }
    }
    .darwin-toggle__track {
      background: var(--color-background-gray-subtle);
    }
  }
}
</style>
